import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Sparkles,
  Facebook,
  Instagram,
  Linkedin,
  MessageCircle,
  Zap,
  Target,
  CalendarClock,
  Gauge,
  Smartphone,
  Bot,
  Workflow,
  CalendarDays,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ParticlesBackground from '../components/ParticlesBackground';

const PageHeader = ({ t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-6">
      <Link to="/" className="flex items-center flex-shrink-0">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
          }}
        />
      </Link>
      <div className="flex items-center gap-4">
        <LanguageSwitcher variant="compact" />
        <Link to="/apps" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition hidden md:block">
          Apps
        </Link>
        <Link to="/posti-privacidad" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition">
          {t('posti.footer.privacy', { defaultValue: 'Privacidad' })}
        </Link>
        <Link to="/posti-terminos" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition">
          {t('posti.footer.terms', { defaultValue: 'Términos' })}
        </Link>
        <Link to="/posti-eula" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition">
          {t('posti_legal.eula_link', { defaultValue: 'EULA' })}
        </Link>
      </div>
    </div>
  </header>
);

const BentoCard = ({ className, children }) => (
  <div className={`group relative bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${className}`}>
    {children}
  </div>
);

const PostiPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bento = useMemo(() => t('posti.bento', { returnObjects: true }), [t]);
  const channels = useMemo(() => t('posti.channels', { returnObjects: true }), [t]);
  const featureList = useMemo(() => t('posti.features_list', { returnObjects: true }), [t]);
  const gridFeatures = useMemo(() => t('posti.grid_features', { returnObjects: true }), [t]);
  const feed = useMemo(() => t('posti.bento.feed', { returnObjects: true }), [t]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <ParticlesBackground />
      <PageHeader t={t} />

      <main className="pt-32 pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/apps'))}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('posti.back_to_apps', { defaultValue: 'Volver a Apps' })}
          </button>

          {/* Hero Section */}
          <div className="text-center mb-16 max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center p-3 bg-violet-100 text-violet-700 rounded-2xl mb-6 shadow-sm">
              <Sparkles size={36} />
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-[#2d2a26] mb-6 tracking-tight">
              {t('posti.title', { defaultValue: 'Posti' })}
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-4 font-medium">
              {t('posti.subtitle', { defaultValue: 'La herramienta de publicación para Bitrix24 que organiza contenido, aprobaciones y calendario en un solo lugar.' })}
            </p>
            <p className="text-sm text-gray-500 mb-8 font-medium">
              {t('posti.description', { defaultValue: 'Pensada para equipos de marketing, comunicación y ventas que necesitan publicar mejor en Facebook, Instagram y LinkedIn sin salir de su operación diaria.' })}
            </p>

            <div className="flex flex-wrap justify-center gap-4 text-sm font-bold">
              <div className="flex items-center gap-2 px-5 py-2.5 bg-blue-50 text-blue-700 rounded-full shadow-sm border border-blue-100 hover:bg-blue-100 transition cursor-default">
                <Facebook size={20} /> {channels?.[0] || 'Facebook'}
              </div>
              <div className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-50 to-orange-50 text-pink-700 rounded-full shadow-sm border border-pink-100 hover:from-pink-100 hover:to-orange-100 transition cursor-default">
                <Instagram size={20} /> {channels?.[1] || 'Instagram'}
              </div>
              <div className="flex items-center gap-2 px-5 py-2.5 bg-sky-50 text-sky-700 rounded-full shadow-sm border border-sky-100 hover:bg-sky-100 transition cursor-default">
                <Linkedin size={20} /> {channels?.[2] || 'LinkedIn'}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-3 text-xs font-semibold text-gray-600">
              {featureList.map((item) => (
                <span key={item} className="px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm">{item}</span>
              ))}
            </div>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <BentoCard className="md:col-span-2 bg-gradient-to-br from-indigo-50/50 to-white flex flex-col justify-between">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-indigo-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <Bot size={16} /> {bento.editor.badge}
                </div>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-3">{bento.editor.title}</h2>
                <p className="text-gray-600">
                  {bento.editor.desc}
                </p>
              </div>

              <div className="mt-auto relative w-full h-48 bg-white border border-gray-200 rounded-xl shadow-inner overflow-hidden flex">
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg shadow-md border border-indigo-100 flex items-center gap-2 z-10">
                  <Zap size={14} className="text-indigo-600" />
                  <span className="text-xs font-extrabold text-indigo-900">{bento.editor.extra}</span>
                </div>

                <div className="w-1/3 border-r border-gray-100 p-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600"><Facebook size={12} /></div>
                    <div className="h-2 w-12 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="h-2 w-full bg-blue-200 rounded-full"></div>
                  <div className="h-2 w-3/4 bg-blue-100 rounded-full"></div>
                  <div className="mt-auto flex gap-1 flex-wrap">
                    <span className="px-2 py-1 text-[9px] font-bold bg-blue-50 text-blue-700 rounded-md">#campana</span>
                    <span className="px-2 py-1 text-[9px] font-bold bg-blue-50 text-blue-700 rounded-md">clc.fyi/post</span>
                  </div>
                </div>
                <div className="w-1/3 border-r border-gray-100 p-4 flex flex-col gap-3 bg-gradient-to-b from-transparent to-pink-50/30">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center text-pink-600"><Instagram size={12} /></div>
                    <div className="h-2 w-12 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="h-24 w-full bg-gray-100 rounded-lg mt-2"></div>
                  <div className="mt-auto flex gap-1 flex-wrap">
                    <span className="px-2 py-1 text-[9px] font-bold bg-pink-50 text-pink-700 rounded-md">#marca</span>
                    <span className="px-2 py-1 text-[9px] font-bold bg-pink-50 text-pink-700 rounded-md">#producto</span>
                  </div>
                </div>
                <div className="w-1/3 p-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center text-sky-600"><Linkedin size={12} /></div>
                    <div className="h-2 w-12 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="h-2 w-full bg-sky-200 rounded-full"></div>
                  <div className="h-2 w-full bg-sky-100 rounded-full"></div>
                  <div className="h-2 w-1/2 bg-sky-100 rounded-full"></div>
                  <div className="mt-auto px-2 py-1 text-[9px] font-bold bg-sky-50 text-sky-700 rounded-md w-fit">Version ejecutiva</div>
                </div>
              </div>
            </BentoCard>

            <BentoCard className="bg-gradient-to-br from-cyan-50/50 to-white flex flex-col justify-between">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-cyan-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <Workflow size={16} /> {bento.workflow.badge}
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-3">{bento.workflow.title}</h2>
                <p className="text-gray-600 text-sm">
                  {bento.workflow.desc}
                </p>
              </div>

              <div className="mt-auto flex flex-col items-center justify-center gap-3 bg-white p-4 rounded-xl border border-cyan-100 shadow-sm">
                <div className="flex items-center justify-between w-full px-2">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center"><MessageCircle size={14} /></div>
                    <span className="text-[9px] font-bold text-gray-500">{bento.workflow.steps[0]}</span>
                  </div>
                  <ArrowRight size={16} className="text-cyan-300" />
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center"><Workflow size={14} /></div>
                    <span className="text-[9px] font-bold text-gray-500">{bento.workflow.steps[1]}</span>
                  </div>
                  <ArrowRight size={16} className="text-cyan-300" />
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><CheckCircle2 size={14} /></div>
                    <span className="text-[9px] font-bold text-emerald-700">{bento.workflow.steps[2]}</span>
                  </div>
                </div>
              </div>
            </BentoCard>

            <BentoCard className="bg-gradient-to-br from-violet-50/50 to-white flex flex-col justify-between">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-violet-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <CalendarClock size={16} /> {bento.calendar.badge}
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-3">{bento.calendar.title}</h2>
                <p className="text-gray-600 text-sm">
                  {bento.calendar.desc}
                </p>
              </div>

              <div className="mt-auto bg-white p-4 rounded-xl border border-violet-100 shadow-sm">
                <div className="grid grid-cols-7 gap-1.5">
                  {Array.from({ length: 28 }).map((_, i) => {
                    const isHot = [9, 10, 16, 17, 24].includes(i);
                    const isWarm = [2, 3, 8, 11, 15, 23, 25].includes(i);
                    let bgClass = "bg-violet-50";
                    if (isHot) bgClass = "bg-violet-500 shadow-[0_0_8px_rgba(139,92,246,0.6)] scale-110 z-10 relative rounded-sm";
                    else if (isWarm) bgClass = "bg-violet-300 rounded-sm";
                    else bgClass = "bg-violet-100 rounded-sm";

                    return <div key={i} className={`h-6 ${bgClass} transition-transform duration-300 group-hover:scale-105`}></div>;
                  })}
                </div>
              </div>
            </BentoCard>

            <BentoCard className="md:col-span-2 bg-gradient-to-br from-blue-50/50 to-white flex flex-col justify-between">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-blue-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <CalendarDays size={16} /> {bento.weekly.badge}
                </div>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-3">{bento.weekly.title}</h2>
                <p className="text-gray-600">
                  {bento.weekly.desc}
                </p>
              </div>

              <div className="mt-auto bg-white p-4 rounded-xl border border-blue-100 shadow-sm overflow-hidden">
                <div className="flex gap-4">
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="text-[10px] font-bold text-gray-400 text-center mb-1">{bento.weekly.days?.[0] || 'LUN 12'}</div>
                    <div className="bg-blue-50 border border-blue-100 p-2 rounded-lg flex items-center gap-2 shadow-sm relative overflow-hidden">
                      <div className="w-1 h-full bg-blue-400 absolute left-0 top-0"></div>
                      <Linkedin size={12} className="text-blue-600 ml-1" />
                      <div className="h-1.5 w-12 bg-blue-200 rounded-full"></div>
                      <CheckCircle2 size={12} className="text-emerald-500 ml-auto" />
                    </div>
                    <div className="bg-pink-50 border border-pink-100 p-2 rounded-lg flex items-center gap-2 shadow-sm relative overflow-hidden">
                      <div className="w-1 h-full bg-pink-400 absolute left-0 top-0"></div>
                      <Instagram size={12} className="text-pink-600 ml-1" />
                      <div className="h-1.5 w-8 bg-pink-200 rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="text-[10px] font-bold text-gray-400 text-center mb-1">{bento.weekly.days?.[1] || 'MAR 13'}</div>
                    <div className="bg-white border border-amber-100 p-2 rounded-lg flex items-center gap-2 shadow-sm relative overflow-hidden">
                      <div className="w-1 h-full bg-amber-400 absolute left-0 top-0"></div>
                      <Facebook size={12} className="text-blue-600 ml-1" />
                      <div className="h-1.5 w-10 bg-amber-100 rounded-full"></div>
                      <span className="ml-auto text-[9px] font-bold text-amber-700">{bento.weekly.review_short || 'Rev.'}</span>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 p-2 rounded-lg flex items-center gap-2 shadow-sm relative overflow-hidden opacity-80 border-dashed">
                      <Instagram size={12} className="text-gray-400 ml-1" />
                      <div className="h-1.5 w-10 bg-gray-200 rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col gap-2 hidden sm:flex">
                    <div className="text-[10px] font-bold text-gray-400 text-center mb-1">{bento.weekly.days?.[2] || 'MIE 14'}</div>
                    <div className="bg-sky-50 border border-sky-100 p-2 rounded-lg flex items-center gap-2 shadow-sm relative overflow-hidden">
                      <div className="w-1 h-full bg-sky-400 absolute left-0 top-0"></div>
                      <Linkedin size={12} className="text-sky-600 ml-1" />
                      <div className="h-1.5 w-12 bg-sky-200 rounded-full"></div>
                    </div>
                    <div className="bg-white border border-emerald-100 p-2 rounded-lg flex items-center gap-2 shadow-sm relative overflow-hidden">
                      <div className="w-1 h-full bg-emerald-400 absolute left-0 top-0"></div>
                      <Facebook size={12} className="text-blue-600 ml-1" />
                      <div className="h-1.5 w-8 bg-emerald-100 rounded-full"></div>
                      <CheckCircle2 size={12} className="text-emerald-500 ml-auto" />
                    </div>
                  </div>
                </div>
              </div>
            </BentoCard>

            <BentoCard className="md:col-span-2 bg-gradient-to-br from-rose-50/50 to-white flex flex-col justify-between">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-rose-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <Workflow size={16} /> {bento.approvals.badge}
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-3">{bento.approvals.title}</h2>
                <p className="text-gray-600 text-sm">
                  {bento.approvals.desc}
                </p>
              </div>

              <div className="mt-auto grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded-xl border border-rose-100 shadow-sm">
                <div className="rounded-2xl border border-rose-100 bg-rose-50/50 p-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-rose-600 mb-3">{bento.approvals.pipeline_title}</div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-xs font-bold text-gray-700">
                      <span className="w-3 h-3 rounded-full bg-gray-300"></span> {bento.approvals.pipeline[0]}
                    </div>
                    <div className="flex items-center gap-3 text-xs font-bold text-amber-700">
                      <span className="w-3 h-3 rounded-full bg-amber-400"></span> {bento.approvals.pipeline[1]}
                    </div>
                    <div className="flex items-center gap-3 text-xs font-bold text-emerald-700">
                      <span className="w-3 h-3 rounded-full bg-emerald-400"></span> {bento.approvals.pipeline[2]}
                    </div>
                    <div className="flex items-center gap-3 text-xs font-bold text-rose-700">
                      <span className="w-3 h-3 rounded-full bg-rose-400"></span> {bento.approvals.pipeline[3]}
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{bento.approvals.review_title}</div>
                    <span className="px-2 py-1 text-[10px] font-bold bg-amber-50 text-amber-700 rounded-md">{bento.approvals.review_status}</span>
                  </div>
                  <div className="space-y-3">
                    <div className="rounded-xl border border-gray-100 px-3 py-2 flex items-center justify-between">
                      <span className="text-[11px] font-bold text-gray-700">{bento.approvals.review_items[0]}</span>
                      <CheckCircle2 size={14} className="text-emerald-500" />
                    </div>
                    <div className="rounded-xl border border-gray-100 px-3 py-2 flex items-center justify-between">
                      <span className="text-[11px] font-bold text-gray-700">{bento.approvals.review_items[1]}</span>
                      <CheckCircle2 size={14} className="text-emerald-500" />
                    </div>
                    <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2 flex items-center justify-between">
                      <span className="text-[11px] font-bold text-amber-800">{bento.approvals.review_items[2]}</span>
                      <span className="text-[10px] font-bold text-amber-700">{bento.approvals.review_waiting}</span>
                    </div>
                  </div>
                </div>
              </div>
            </BentoCard>

            <BentoCard className="bg-gradient-to-br from-amber-50/50 to-white flex flex-col justify-between">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-amber-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <Smartphone size={16} /> {bento.preview.badge}
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-3">{bento.preview.title}</h2>
                <p className="text-gray-600 text-sm">
                  {bento.preview.desc}
                </p>
              </div>

              <div className="mt-auto bg-white p-4 rounded-xl border border-amber-100 shadow-sm flex items-center justify-center gap-3">
                <div className="w-20 h-36 rounded-[20px] border-4 border-gray-900 bg-gray-50 p-2 flex flex-col gap-2">
                  <div className="h-2 w-8 bg-gray-200 rounded-full"></div>
                  <div className="h-14 bg-pink-100 rounded-xl"></div>
                  <div className="h-2 w-full bg-gray-200 rounded-full"></div>
                  <div className="h-2 w-3/4 bg-gray-100 rounded-full"></div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-blue-700"><Facebook size={14} /> {bento.preview.networks[0]}</div>
                  <div className="flex items-center gap-2 text-xs font-bold text-pink-700"><Instagram size={14} /> {bento.preview.networks[1]}</div>
                  <div className="flex items-center gap-2 text-xs font-bold text-sky-700"><Linkedin size={14} /> {bento.preview.networks[2]}</div>
                </div>
              </div>
            </BentoCard>

            <BentoCard className="bg-gradient-to-br from-emerald-50/50 to-white flex flex-col justify-between">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-emerald-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <Target size={16} /> {bento.assets.badge}
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-3">{bento.assets.title}</h2>
                <p className="text-gray-600 text-sm">
                  {bento.assets.desc}
                </p>
              </div>

              <div className="mt-auto grid grid-cols-3 gap-2 bg-white p-3 rounded-xl border border-emerald-100 shadow-sm">
                <div className="h-20 rounded-xl bg-emerald-100"></div>
                <div className="h-20 rounded-xl bg-pink-100"></div>
                <div className="h-20 rounded-xl bg-sky-100"></div>
                <div className="h-10 rounded-xl bg-amber-100"></div>
                <div className="h-10 rounded-xl bg-violet-100"></div>
                <div className="h-10 rounded-xl bg-gray-100"></div>
              </div>
            </BentoCard>

            <BentoCard className="md:col-span-2 bg-gradient-to-br from-orange-50/50 to-white flex flex-col justify-between">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-orange-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <MessageCircle size={16} /> {feed.badge || 'Bitrix24 Feed'}
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-3">{feed.title || 'Publicacion en Feed de noticias'}</h2>
                <p className="text-gray-600 text-sm">
                  {feed.desc || 'Con Posti, tambien puedes publicar en el Feed de noticias de Bitrix24 para mantener al equipo alineado con anuncios clave, avances y novedades operativas desde un solo lugar.'}
                </p>
              </div>

              <div className="mt-auto grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded-xl border border-orange-100 shadow-sm">
                <div className="rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-orange-50 p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-orange-200 flex items-center justify-center text-orange-800">
                      <MessageCircle size={18} />
                    </div>
                    <div>
                      <div className="text-sm font-extrabold text-gray-900">{feed.card_title || 'Entrada al Feed'}</div>
                      <div className="text-[11px] font-medium text-gray-500">{feed.card_subtitle || 'Comunicacion interna centralizada'}</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between rounded-xl bg-white border border-gray-100 px-3 py-2">
                      <span className="text-[11px] font-bold text-gray-700">{feed.items?.[0]?.label || 'Anuncio estrategico'}</span>
                      <span className="text-[10px] font-bold text-emerald-600">{feed.items?.[0]?.status || 'Publicado'}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-white border border-gray-100 px-3 py-2">
                      <span className="text-[11px] font-bold text-gray-700">{feed.items?.[1]?.label || 'Comunicado de equipo'}</span>
                      <span className="text-[10px] font-bold text-emerald-600">{feed.items?.[1]?.status || 'Visible'}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-white border border-gray-100 px-3 py-2">
                      <span className="text-[11px] font-bold text-gray-700">{feed.items?.[2]?.label || 'Actualizacion operativa'}</span>
                      <span className="text-[10px] font-bold text-amber-600">{feed.items?.[2]?.status || 'Programado'}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-orange-50 p-4 flex flex-col justify-between">
                  <div>
                    <div className="text-sm font-extrabold text-gray-900 mb-3">{feed.impact_title || 'Impacto en el equipo'}</div>
                    <div className="flex gap-2 mb-4">
                      <div className="h-9 flex-1 rounded-xl bg-[#141414]"></div>
                      <div className="h-9 flex-1 rounded-xl bg-[#f59e0b]"></div>
                      <div className="h-9 flex-1 rounded-xl bg-[#fb7185]"></div>
                      <div className="h-9 flex-1 rounded-xl bg-[#38bdf8]"></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-xl bg-white border border-gray-100 px-3 py-2">
                        <div className="text-[10px] font-bold text-gray-400 mb-1">{feed.meta?.[0]?.label || 'Audiencia'}</div>
                        <div className="text-xs font-extrabold text-gray-800">{feed.meta?.[0]?.value || 'Equipos y areas'}</div>
                      </div>
                      <div className="rounded-xl bg-white border border-gray-100 px-3 py-2">
                        <div className="text-[10px] font-bold text-gray-400 mb-1">{feed.meta?.[1]?.label || 'Objetivo'}</div>
                        <div className="text-xs font-extrabold text-gray-800">{feed.meta?.[1]?.value || 'Informar y alinear rapido'}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap mt-4">
                    <span className="px-2.5 py-1.5 text-[10px] font-bold bg-orange-50 text-orange-700 rounded-lg">{feed.tags?.[0] || 'Feed Bitrix24'}</span>
                    <span className="px-2.5 py-1.5 text-[10px] font-bold bg-rose-50 text-rose-700 rounded-lg">{feed.tags?.[1] || 'Comunicado interno'}</span>
                    <span className="px-2.5 py-1.5 text-[10px] font-bold bg-sky-50 text-sky-700 rounded-lg">{feed.tags?.[2] || 'Actualizacion clave'}</span>
                  </div>
                </div>
              </div>
            </BentoCard>

            <BentoCard className="md:col-span-2 lg:col-span-3 bg-gradient-to-br from-sky-50/50 to-white flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-1/2">
                <div className="flex items-center gap-2 text-sky-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <Gauge size={16} /> {bento.performance.badge}
                </div>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-3">{bento.performance.title}</h2>
                <p className="text-gray-600 mb-4">
                  {bento.performance.desc}
                </p>
                <p className="text-gray-600 text-sm">
                  {bento.performance.desc_extra}
                </p>
              </div>

              <div className="lg:w-1/2 w-full flex flex-wrap justify-center gap-4 p-4">
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-sky-100 flex flex-col items-center justify-center w-32 h-32">
                  <span className="text-xs font-bold text-sky-500 mb-2">{bento.performance.metrics[0].label}</span>
                  <span className="text-2xl font-black text-gray-800">{bento.performance.metrics[0].val}</span>
                  <span className="text-[10px] font-bold text-gray-400 text-center">{bento.performance.metrics[0].hint}</span>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-emerald-100 flex flex-col items-center justify-center w-32 h-32">
                  <span className="text-xs font-bold text-emerald-500 mb-2">{bento.performance.metrics[1].label}</span>
                  <span className="text-2xl font-black text-gray-800">{bento.performance.metrics[1].val}</span>
                  <span className="text-[10px] font-bold text-gray-400 text-center">{bento.performance.metrics[1].hint}</span>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-purple-100 flex flex-col items-center justify-center w-32 h-32">
                  <span className="text-xs font-bold text-purple-500 mb-2">{bento.performance.metrics[2].label}</span>
                  <span className="text-2xl font-black text-gray-800">{bento.performance.metrics[2].val}</span>
                  <span className="text-[10px] font-bold text-gray-400 text-center">{bento.performance.metrics[2].hint}</span>
                </div>
              </div>
            </BentoCard>

          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-4 gap-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white px-6 py-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition">
              <div className="p-3 bg-gray-100 text-gray-700 rounded-xl flex-shrink-0"><Zap size={24} /></div>
              <div>
                <div className="font-extrabold text-gray-900 text-base mb-1">{gridFeatures[0].title}</div>
                <div className="text-xs text-gray-500">{gridFeatures[0].desc}</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white px-6 py-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition">
              <div className="p-3 bg-amber-100 text-amber-700 rounded-xl flex-shrink-0"><CalendarDays size={24} /></div>
              <div>
                <div className="font-extrabold text-gray-900 text-base mb-1">{gridFeatures[1].title}</div>
                <div className="text-xs text-gray-500">{gridFeatures[1].desc}</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white px-6 py-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition">
              <div className="p-3 bg-blue-100 text-blue-700 rounded-xl flex-shrink-0"><Smartphone size={24} /></div>
              <div>
                <div className="font-extrabold text-gray-900 text-base mb-1">{gridFeatures[2].title}</div>
                <div className="text-xs text-gray-500">{gridFeatures[2].desc}</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white px-6 py-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition">
              <div className="p-3 bg-violet-100 text-violet-700 rounded-xl flex-shrink-0"><Target size={24} /></div>
              <div>
                <div className="font-extrabold text-gray-900 text-base mb-1">{gridFeatures[3].title}</div>
                <div className="text-xs text-gray-500">{gridFeatures[3].desc}</div>
              </div>
            </div>
          </div>

          {/* Enlaces Legales */}
          <div className="mt-20 pt-8 border-t border-gray-200 text-center flex flex-col sm:flex-row items-center justify-center gap-6">
            <span className="text-gray-500 text-sm font-medium">Posti &copy; {new Date().getFullYear()}</span>
            <Link to="/posti-privacidad" className="text-sm font-bold text-gray-400 hover:text-indigo-600 transition">
              {t('posti.footer.privacy', { defaultValue: 'Política de Privacidad' })}
            </Link>
            <Link to="/posti-terminos" className="text-sm font-bold text-gray-400 hover:text-indigo-600 transition">
              {t('posti.footer.terms', { defaultValue: 'Términos de Uso' })}
            </Link>
            <Link to="/posti-eula" className="text-sm font-bold text-gray-400 hover:text-indigo-600 transition">
              {t('posti_legal.eula_link', { defaultValue: 'EULA' })}
            </Link>
          </div>

        </div>
      </main>
    </div>
  );
};

export default PostiPage;
